import { FC } from 'react';
import { createZodFormResolver, Form, lt } from '@mews/framework';
import { SchemaFormFields } from '@mews-commander/form';
import { useCommanderSelector } from '@mews-commander/utils';
import { isServiceBusinessSegmentRequired } from '@mews-commander/legacy-entities/src/ducks/services';
import { TextKey } from '@mews-commander/enums';
import { FormLayout } from '../../FormLayout';
import { getBusinessSegmentInitialValues } from './selectors';
import { BusinessSegmentFormSchema, BusinessSegmentFormValues } from './types';
import { useBusinessSegmentOptions } from './useBusinessSegmentOptions';
import { useUpdateBusinessSegmentMutation } from './useUpdateBusinessSegmentMutation';

interface BusinessSegmentFormProps {
    scope: string;
    serviceId: string;
    reservationIds: string[];
    onSuccess: () => void;
}

export const BusinessSegmentForm: FC<BusinessSegmentFormProps> = ({ scope, serviceId, reservationIds, onSuccess }) => {
    const defaultValues = useCommanderSelector((state) => getBusinessSegmentInitialValues(state, { scope }));
    const showEmptyValue = useCommanderSelector((state) => !isServiceBusinessSegmentRequired(state, { id: serviceId }));
    const mutation = useUpdateBusinessSegmentMutation(reservationIds);
    const options = useBusinessSegmentOptions(serviceId);

    const onSubmit = (values: BusinessSegmentFormValues) => {
        mutation.mutate(values, { onSuccess });
    };

    return (
        <Form
            onSubmit={onSubmit}
            resolver={createZodFormResolver(BusinessSegmentFormSchema)}
            values={defaultValues}
            dataTestForm="business-segment-form"
        >
            <FormLayout loading={mutation.isPending} submitButtonDisabled={false} vertical={false}>
                <SchemaFormFields
                    schema={BusinessSegmentFormSchema}
                    formDefinition={{
                        fields: {
                            businessSegmentId: {
                                label: lt(TextKey.BusinessSegment),
                                field: {
                                    type: 'select',
                                    options,
                                    showEmptyValue,
                                    emptyValueAsNull: true,
                                },
                            },
                        },
                    }}
                />
            </FormLayout>
        </Form>
    );
};
